<template>
  <div>
    <project-toolbar :selectMonth="isAzure ? true : false">
      <button
        v-if="isAzure && invoice_usages_downloader"
        class="md-button md-size-small box-right-edge"
        @click="downloadUsages"
      >
        {{ L('download') }}
      </button>
    </project-toolbar>
    <md-table
      v-model="invoiceFiltered"
      md-stripe
      :md-sort.sync="sort.sort"
      :md-sort-order.sync="sort.sortOrder"
      md-fixed-header
      :md-height="window.height"
      ref="dataTable"
    >
      <md-table-row slot="md-table-row" slot-scope="{ item }">
        <md-table-cell :md-label="L('seq')" md-sort-by="seq">{{
          item.seq
        }}</md-table-cell>
        <md-table-cell :md-label="L('name')" md-sort-by="name">{{
          item.name
        }}</md-table-cell>
        <md-table-cell
          :md-label="L('invoiceMonth')"
          md-sort-by="invoice_month"
          >{{ item.invoice_month }}</md-table-cell
        >
        <md-table-cell :md-label="L('originalCost')">
          {{
            item.original_costs
              .map((c) => currency(c.cost + c.discount, c.currency))
              .filter((c) => c !== 0)
              .join(' / ') || 0
          }}
        </md-table-cell>
        <md-table-cell :md-label="L('discount')" md-sort-by="discount">
          {{
            item.original_costs
              .map((c) => currency(c.discount, c.currency))
              .filter((c) => c !== 0)
              .join(' / ') || 0
          }}
        </md-table-cell>
        <md-table-cell
          v-if="$config.feature.showExchangeRate"
          :md-label="L('exchangeRate')"
        >
          {{ item.exchange_rate | round2 }}
        </md-table-cell>
        <md-table-cell :md-label="L('cost')">{{
          (item.final_cost - item.vat) | currency(item.charge_currency)
        }}</md-table-cell>
        <md-table-cell :md-label="L('vat')">{{
          Number(item.vat) | currency(item.charge_currency)
        }}</md-table-cell>
        <md-table-cell :md-label="L('finalCost')" md-sort-by="final_cost">{{
          item.final_cost | currency(item.charge_currency)
        }}</md-table-cell>
        <md-table-cell :md-label="L('createdDate')" md-sort-by="created_at">{{
          item.created_at | moment_format('YYYY-MM-DD')
        }}</md-table-cell>
        <md-table-cell :md-label="L('action')" class="action-button">
          <md-button
            class="md-icon-button md-dense"
            @click="downloadInvoice(item)"
          >
            <fa-icon icon="file-alt" />
            <md-tooltip>{{ L('viewInvoice') }} (PDF)</md-tooltip>
          </md-button>
          <md-button
            class="md-icon-button md-dense"
            @click="downloadUsage(item.seq)"
          >
            <fa-icon icon="download" />
            <md-tooltip>{{ L('downloadUsage') }} (CSV)</md-tooltip>
          </md-button>
          <md-button
            class="md-icon-button md-dense"
            @click="viewUsage(item.seq)"
          >
            <fa-icon icon="file-invoice" />
            <md-tooltip>{{ L('viewUsage') }}</md-tooltip>
          </md-button>
          <md-button
            class="md-icon-button md-dense"
            @click="viewReport(item)"
            v-if="
              $config.feature.report && item.report.length > 0 && item.is_report
            "
          >
            <fa-icon icon="file-contract" />
            <md-tooltip>{{ L('viewReport') }}</md-tooltip>
          </md-button>
        </md-table-cell>
      </md-table-row>
    </md-table>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import api from '@/api'
import ProjectToolbar from '@/views/billing/ProjectToolbar'
import { propFilter } from '@/lib/helper'
import { formatDate } from '@/lib/dateutil'
import store from '@/store'

export default {
  components: { ProjectToolbar },

  data: () => ({
    invoice: {
      list: null,
      search: '',
      searchFields: ['seq'],
    },
    invoices: [],
    locale: 'en',

    window: {
      height: 0,
    },

    sort: {
      sort: 'seq',
      sortOrder: 'desc',
    },
  }),

  mounted() {
    this.load()
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.userInfo,
      curMonth: (state) => state.curMonth,
    }),

    invoiceFiltered: {
      get() {
        return propFilter(this.invoice)
      },
      set() {},
    },

    invoiceMonth() {
      return this.curMonth.replace('-', '')
    },

    isAzure() {
      return this.$config.platform.id === 'azure'
    },

    invoice_usages_downloader() {
      return this.userInfo.invoice_usages_downloader
    },
  },

  watch: {
    userInfo() {
      this.load()
    },

    invoiceMonth() {
      this.load()
    },
  },

  methods: {
    load() {
      if (!this.userInfo.seq) {
        return
      }

      api.user.invoice(this.userInfo.seq).then((data) => {
        let formedData = data.map((d) => {
          d.seq = Number(d.seq)
          d.original_cost = Number(d.original_cost)
          d.discount = Number(d.discount)
          d.final_cost = Number(d.final_cost)
          d.logo = this.getLogoName(d.logo)
          d.original_costs.sort((a, b) => a.currency.localeCompare(b.currency))
          return d
        })
        if (this.invoiceMonth && this.isAzure) {
          formedData = formedData.filter(
            (d) => d.invoice_month === this.invoiceMonth
          )
        }

        this.$refs.dataTable.mdSortFn(formedData)
        this.invoice.list = formedData
      })
    },

    downloadUsage(seq) {
      api.download.invoiceUsage(seq, this.userInfo.seq)
    },

    downloadUsages() {
      const seqs = this.invoice.list.map((i) => i.seq)
      if (!seqs.length) {
        store.dispatch('dialog/showAlert', this.L('noData'))
        return
      }
      api.download.invoiceUsages(this.invoiceMonth, seqs)
    },

    downloadInvoice(item) {
      const company = item.company ? item.company : ''
      const invoiceTitle = this.$config.invoice.title
      const settingInfo = JSON.parse(localStorage.getItem('settingInfo')) || {}
      const month =
        settingInfo.language === 'ko'
          ? `${Number(item.invoice_month.substring(4))}${this.L('month')}`
          : formatDate(item.invoice_month, 'MMMM')
      const name = `${item.logo}_${company}_${invoiceTitle}_${month}_${this.L(
        'invoice'
      )}.pdf`
      api.download.invoice(item.seq, name, this.userInfo.seq)
    },

    viewUsage(seq) {
      window.open(
        `/${this.userInfo.seq}/invoice_detail/${seq}/${this.locale}`,
        '_blank'
      )
    },

    windowResized() {
      this.window.height = window.innerHeight - 240
    },

    viewReport(item) {
      if (!item.is_report) {
        store.dispatch('dialog/showAlert', this.L('notFoundReport'))
      } else {
        const company = item.company ? item.company : ''
        const invoiceTitle = this.$config.invoice.title
        const settingInfo =
          JSON.parse(localStorage.getItem('settingInfo')) || {}
        const month =
          settingInfo.language === 'ko'
            ? `${item.invoice_month.substring(0, 4)}년_${Number(item.invoice_month.substring(4))}${this.L('month')}`
            : formatDate(item.invoice_month, 'MMMM_YYYY')
        const name = `${item.logo}_${company}_${invoiceTitle}_${month}_${this.L(
          'report'
        )}.pdf`

        api.download.report(item.seq, name, this.userInfo.seq)
      }
    },

    getLogoName(logo) {
      let name = this.L('megazone')
      if (logo === 'megazone-soft') name = this.L('megazoneSoft')
      else if (logo === 'megazone-cloud') name = this.L('megazoneCloud')
      return name
    },
  },
}
</script>

<style lang="scss" scoped>
.md-content {
  padding: 16px;
}
</style>
